// ── Entity Excel import / export — column spec, file writer, parser ─────────
// One "Entities" sheet, one row per company. Export and import share the same
// column spec (ENTITY_COLS), so an exported file can be edited and imported
// back, and a business can load its existing customer/supplier list in one go.
// Built on the shared machinery in utils/excelImport.ts, like Purchase's and
// Opening Stock's imports. The Import dialog itself is
// components/EntityImportExport.tsx.
//
// Import rules:
//   · Required: Company Name + Type (one or more of Customer, Supplier,
//     Broker, Shipping, Insurance, Lab — comma-separated). Everything else
//     is optional; the Edit Entity form still asks for phone/owner the next
//     time someone edits the record.
//   · A company whose name already exists (case-insensitive), or repeats
//     earlier in the same file, is skipped — never overwritten.
//   · Rows are written with the same field shape EntityFormModal saves.

import { cleanAddress, formatAddressOneLine } from './address';
import { COUNTRIES } from './countries';
import { CURRENCY_LIST } from './currency';
import {
  downloadImportWorkbook, loadWorkbook, readSheetRows, parseImportDate, cleanNum,
  XLSX_GROUP_ARGB, type XlsxCol, type XlsxSampleRow,
} from './excelImport';
import type { Entity, EntityType, KYCStatus, EntityAddress } from '../types';

// Same rules as EntityFormModal's needsKYC / isService (duplicated so this
// pure module doesn't pull in the form component).
const needsKYC  = (types: EntityType[]) => types.includes('customer') || types.includes('supplier');
const isService = (types: EntityType[]) => types.some(t => t === 'shipping' || t === 'insurance' || t === 'lab');

// ── Column spec (same left→right order for export, sample and import) ──────

type F =
  | 'companyName' | 'types' | 'ownerName' | 'ownerPhone' | 'ownerEmail'
  | 'phone' | 'email' | 'accountantEmail' | 'vatGstBtw' | 'country' | 'defaultCurrency'
  | 'defaultTerms' | 'creditLimit' | 'defaultLess' | 'website'
  | 'billName' | 'billLine1' | 'billLine2' | 'billCity' | 'billState' | 'billPostal' | 'billCountry'
  | 'shipSame'
  | 'shipName' | 'shipLine1' | 'shipLine2' | 'shipCity' | 'shipState' | 'shipPostal' | 'shipCountry'
  | 'kycStatus' | 'kycDocType' | 'kycDocNumber' | 'kycExpiry'
  | 'accountNumber' | 'notes' | 'active';

const BILL_ARGB = XLSX_GROUP_ARGB;   // teal — Bill To group
const SHIP_ARGB = 'FF7C3AED';        // violet — Ship To group

const ENTITY_COLS: XlsxCol<F>[] = [
  { header: 'Company Name',      field: 'companyName', width: 30, aliases: ['company', 'name'], note: 'Required' },
  { header: 'Type',              field: 'types',       width: 22, list: 'type', note: 'Required — one or more, comma-separated: Customer, Supplier, Broker, Shipping, Insurance, Lab' },
  { header: 'Owner / Contact Name', field: 'ownerName', width: 22, aliases: ['owner name', 'contact name', 'owner'] },
  { header: 'Owner Phone',       field: 'ownerPhone',  width: 16, aliases: ['contact phone'] },
  { header: 'Owner Email',       field: 'ownerEmail',  width: 24, aliases: ['contact email'] },
  { header: 'Company Phone',     field: 'phone',       width: 16, aliases: ['phone'] },
  { header: 'Company Email',     field: 'email',       width: 26, aliases: ['email'] },
  { header: 'Accountant Email',  field: 'accountantEmail', width: 24 },
  { header: 'VAT / GST / BTW',   field: 'vatGstBtw',   width: 18, aliases: ['vat', 'gst', 'btw', 'tax id', 'vat no'] },
  { header: 'Country (Tax)',     field: 'country',     width: 18, list: 'country', aliases: ['country'] },
  { header: 'Default Currency',  field: 'defaultCurrency', width: 14, list: 'currency', aliases: ['currency'] },
  { header: 'Default Terms (days)', field: 'defaultTerms', width: 14, numeric: true, aliases: ['terms', 'payment terms'], note: 'Customers / suppliers only' },
  { header: 'Credit Limit',      field: 'creditLimit', width: 14, numeric: true, note: 'Customers only' },
  { header: 'Default Keep (%)',  field: 'defaultLess', width: 12, numeric: true, aliases: ['default keep(%)', 'default keep %', 'keep (%)', 'keep(%)', 'keep %', 'default less %', 'less %'], note: 'Customers only. The share he keeps for his own client.' },
  { header: 'Website',           field: 'website',     width: 22 },
  { header: 'Bill To — Name',    field: 'billName',    width: 20, headerArgb: BILL_ARGB },
  { header: 'Bill To — Address Line 1', field: 'billLine1', width: 28, headerArgb: BILL_ARGB, aliases: ['address', 'address line 1'] },
  { header: 'Bill To — Address Line 2', field: 'billLine2', width: 22, headerArgb: BILL_ARGB, aliases: ['address line 2'] },
  { header: 'Bill To — City',    field: 'billCity',    width: 16, headerArgb: BILL_ARGB, aliases: ['city'] },
  { header: 'Bill To — State',   field: 'billState',   width: 14, headerArgb: BILL_ARGB, aliases: ['state'] },
  { header: 'Bill To — Postal Code', field: 'billPostal', width: 12, headerArgb: BILL_ARGB, aliases: ['postal code', 'zip'] },
  { header: 'Bill To — Country', field: 'billCountry', width: 18, headerArgb: BILL_ARGB, list: 'country' },
  { header: 'Ship To Same as Bill To', field: 'shipSame', width: 12, list: 'yesNo', note: 'Yes / No — blank means Yes when no Ship To address is filled' },
  { header: 'Ship To — Name',    field: 'shipName',    width: 20, headerArgb: SHIP_ARGB },
  { header: 'Ship To — Address Line 1', field: 'shipLine1', width: 28, headerArgb: SHIP_ARGB },
  { header: 'Ship To — Address Line 2', field: 'shipLine2', width: 22, headerArgb: SHIP_ARGB },
  { header: 'Ship To — City',    field: 'shipCity',    width: 16, headerArgb: SHIP_ARGB },
  { header: 'Ship To — State',   field: 'shipState',   width: 14, headerArgb: SHIP_ARGB },
  { header: 'Ship To — Postal Code', field: 'shipPostal', width: 12, headerArgb: SHIP_ARGB },
  { header: 'Ship To — Country', field: 'shipCountry', width: 18, headerArgb: SHIP_ARGB, list: 'country' },
  { header: 'KYC Status',        field: 'kycStatus',   width: 16, list: 'kyc', note: 'Customers / suppliers only' },
  { header: 'KYC Document Type', field: 'kycDocType',  width: 20 },
  { header: 'KYC Document No',   field: 'kycDocNumber', width: 18 },
  { header: 'KYC Expiry Date',   field: 'kycExpiry',   width: 14, date: true },
  { header: 'Account Number',    field: 'accountNumber', width: 18, note: 'Shipping / insurance / lab only' },
  { header: 'Notes',             field: 'notes',       width: 30 },
  { header: 'Active',            field: 'active',      width: 10, list: 'yesNo', note: 'Yes / No — blank means Yes' },
];

export const TYPE_LABEL: Record<EntityType, string> = {
  customer: 'Customer', supplier: 'Supplier', broker: 'Broker',
  shipping: 'Shipping', insurance: 'Insurance', lab: 'Lab',
};
const KYC_LABEL: Record<KYCStatus, string> = {
  'not-submitted': 'Not Submitted', pending: 'Pending Review', verified: 'Verified',
};
const TYPE_LIST = [
  'Customer', 'Supplier', 'Broker', 'Customer, Supplier', 'Shipping', 'Insurance', 'Lab',
];

function lists(): Record<string, string[]> {
  return {
    type: TYPE_LIST,
    country: COUNTRIES,
    currency: CURRENCY_LIST.map(c => c.code),
    kyc: Object.values(KYC_LABEL),
    yesNo: ['Yes', 'No'],
  };
}

const README = [
  'How to import your companies (customers, suppliers, brokers, service providers)',
  '1. Fill one row per company on the "Entities" sheet. Delete the example rows first.',
  '2. Required: Company Name and Type. Type can hold more than one, comma-separated, e.g. "Customer, Supplier".',
  '3. Everything else is optional. Teal columns are the Bill To address, violet ones the Ship To address.',
  '4. Leave "Ship To Same as Bill To" blank (or Yes) if they ship to the billing address.',
  '5. A company whose name already exists in the program is skipped, never overwritten.',
  '6. Save as .xlsx, then use the Import button on the Entity page.',
  'Tip: Export gives you this same file with all your current companies — handy as a backup or to edit in bulk.',
];

// ── Export ──────────────────────────────────────────────────────────────────

const addrRow = (a: EntityAddress | undefined, p: 'bill' | 'ship'): XlsxSampleRow<F> => ({
  [`${p}Name`]: a?.name ?? '', [`${p}Line1`]: a?.line1 ?? '', [`${p}Line2`]: a?.line2 ?? '',
  [`${p}City`]: a?.city ?? '', [`${p}State`]: a?.state ?? '', [`${p}Postal`]: a?.postalCode ?? '',
  [`${p}Country`]: a?.country ?? '',
} as XlsxSampleRow<F>);

function entityToRow(e: Entity): XlsxSampleRow<F> {
  // A legacy entity with only the old free-text address exports it as Line 1.
  const billTo = e.billTo ?? (e.address ? { line1: e.address } : undefined);
  return {
    companyName: e.companyName, types: e.types.map(t => TYPE_LABEL[t]).join(', '),
    ownerName: e.ownerName ?? '', ownerPhone: e.ownerPhone ?? '', ownerEmail: e.ownerEmail ?? '',
    phone: e.phone ?? '', email: e.email ?? '', accountantEmail: e.accountantEmail ?? '',
    vatGstBtw: e.vatGstBtw ?? '', country: e.country ?? '', defaultCurrency: e.defaultCurrency ?? '',
    defaultTerms: e.defaultTerms ?? '', creditLimit: e.creditLimitUSD ?? '', defaultLess: e.defaultLessPercent ?? '', website: e.website ?? '',
    ...addrRow(billTo, 'bill'),
    shipSame: e.shipToSameAsBillTo || !e.shipTo ? 'Yes' : 'No',
    ...addrRow(e.shipToSameAsBillTo ? undefined : e.shipTo, 'ship'),
    kycStatus: needsKYC(e.types) ? KYC_LABEL[e.kycStatus] ?? '' : '',
    kycDocType: e.kycDocType ?? '', kycDocNumber: e.kycDocNumber ?? '',
    kycExpiry: e.kycExpiryDate ? new Date(`${e.kycExpiryDate}T00:00:00Z`) : '',
    accountNumber: e.accountNumber ?? '', notes: e.notes ?? '',
    active: e.isActive === false ? 'No' : 'Yes',
  };
}

function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Every given entity, in the import format (so the file can be imported back). */
export function exportEntitiesXlsx(entities: Entity[]): Promise<void> {
  return downloadImportWorkbook<F>({
    fileName: `Entities-${localToday()}.xlsx`,
    sheets: [{ name: 'Entities', cols: ENTITY_COLS, sample: entities.map(entityToRow), tabArgb: 'FF4F46E5' }],
    lists: lists(),
    // Real data — never swap a value for a dropdown option.
    freeLists: Object.keys(lists()),
    readme: README,
    rows: Math.max(300, entities.length + 100),
  });
}

export function downloadEntitySample(): Promise<void> {
  return downloadImportWorkbook<F>({
    fileName: 'Entity-Import-Sample.xlsx',
    sheets: [{
      name: 'Entities', cols: ENTITY_COLS, tabArgb: 'FF4F46E5',
      sample: [
        {
          companyName: 'Example Diamonds BV', types: 'Customer', ownerName: 'Jan de Vries', ownerPhone: '+32 470 12 34 56',
          ownerEmail: 'jan@example.com', phone: '+32 3 123 45 67', email: 'info@example.com', vatGstBtw: 'BE0123456789',
          country: 'Belgium', defaultCurrency: 'EUR', defaultTerms: 30, creditLimit: 50000,
          billLine1: 'Hoveniersstraat 30', billCity: 'Antwerp', billPostal: '2018', billCountry: 'Belgium', shipSame: 'Yes',
          kycStatus: 'Verified', active: 'Yes',
        },
        {
          companyName: 'Sample Gems Pvt Ltd', types: 'Customer, Supplier', ownerName: 'Ravi Shah', ownerPhone: '+91 98200 00000',
          phone: '+91 22 2363 0000', email: 'accounts@samplegems.in', country: 'India', defaultCurrency: 'INR', defaultTerms: 45,
          billLine1: 'Bharat Diamond Bourse, BKC', billCity: 'Mumbai', billState: 'Maharashtra', billPostal: '400051', billCountry: 'India',
          shipSame: 'No', shipLine1: 'Opera House', shipCity: 'Mumbai', shipCountry: 'India',
          kycStatus: 'Pending Review', active: 'Yes',
        },
        { companyName: 'Fast Courier Ltd', types: 'Shipping', ownerName: 'Desk', ownerPhone: '+44 20 0000 0000', accountNumber: 'ACC-1001' },
      ],
    }],
    lists: lists(),
    freeLists: ['country'],
    readme: README,
  });
}

// ── Import parsing ──────────────────────────────────────────────────────────

export interface ParsedEntity {
  rowNo:   number;
  payload: Record<string, unknown> | null;   // null = can't import (see problem)
  name:    string;
  types:   EntityType[];
  owner:   string;
  phone:   string;
  country: string;
  problem: string;                            // '' = ready
}

const yes = (v: string) => /^(y|yes|true|1|ja|oui)$/i.test(v.trim());
const no  = (v: string) => /^(n|no|false|0|nee|non)$/i.test(v.trim());

function parseTypes(v: string, warnings: string[], where: string): EntityType[] {
  const out: EntityType[] = [];
  v.split(/[,;/&+]|\band\b/i).map(s => s.trim().toLowerCase()).filter(Boolean).forEach(s => {
    const hit = (Object.keys(TYPE_LABEL) as EntityType[]).find(t => t === s || TYPE_LABEL[t].toLowerCase() === s
      || (t === 'shipping' && /^(shipper|courier|logistics)$/.test(s))
      || (t === 'insurance' && /^insurer$/.test(s)));
    if (hit) { if (!out.includes(hit)) out.push(hit); }
    else warnings.push(`${where}: Type "${s}" not recognised — ignored.`);
  });
  return out;
}

function matchList(v: string, options: readonly string[]): string | null {
  if (!v) return '';
  return options.find(o => o.toLowerCase() === v.toLowerCase()) ?? null;
}

export async function parseEntitiesXlsx(data: ArrayBuffer, existing: Entity[]): Promise<{ rows: ParsedEntity[]; warnings: string[] }> {
  const wb = await loadWorkbook(data);
  const warnings: string[] = [];
  const sheetRows = readSheetRows(wb, 'Entities', n => n.includes('entit') || n.includes('compan') || n.includes('customer'), ENTITY_COLS, warnings)
    ?? (wb.worksheets[0] ? readSheetRows(wb, wb.worksheets[0].name, () => false, ENTITY_COLS, warnings) : null)
    ?? [];

  const taken = new Set(existing.map(e => e.companyName.trim().toLowerCase()));
  const inFile = new Set<string>();
  const rows: ParsedEntity[] = [];

  for (const r of sheetRows) {
    const t = (f: F) => (r.text[f] ?? '').trim();
    const where = `Row ${r.rowNo}`;
    const name  = t('companyName');
    const types = parseTypes(t('types'), warnings, where);
    const base = { rowNo: r.rowNo, name, types, owner: t('ownerName'), phone: t('phone') || t('ownerPhone'), country: t('country') || t('billCountry') };

    if (!name)              { rows.push({ ...base, payload: null, problem: 'Company Name missing' }); continue; }
    if (types.length === 0) { rows.push({ ...base, payload: null, problem: 'Type missing' }); continue; }
    if (taken.has(name.toLowerCase())) {
      rows.push({ ...base, payload: null, problem: 'Already exists — skipped' }); continue;
    }
    if (inFile.has(name.toLowerCase())) {
      rows.push({ ...base, payload: null, problem: 'Repeated in this file — skipped' }); continue;
    }
    inFile.add(name.toLowerCase());

    const country = (f: F) => {
      const v = t(f);
      const hit = matchList(v, COUNTRIES);
      if (hit === null) warnings.push(`${where}: Country "${v}" isn't in the list — kept as typed.`);
      return hit || v;
    };
    const addr = (p: 'bill' | 'ship'): EntityAddress => ({
      name: t(`${p}Name`), line1: t(`${p}Line1`), line2: t(`${p}Line2`), city: t(`${p}City`),
      state: t(`${p}State`), postalCode: t(`${p}Postal`), country: country(`${p}Country`),
    });
    const billTo = cleanAddress(addr('bill'));
    const shipCandidate = cleanAddress(addr('ship'));
    // Separate Ship To only when one is actually filled in and the file
    // doesn't say "Yes, same as billing"; otherwise mirror Bill To.
    const shipSame = !shipCandidate || yes(t('shipSame'));
    const shipTo = shipSame ? null : shipCandidate;

    let currency = t('defaultCurrency').toUpperCase();
    if (currency && !CURRENCY_LIST.some(c => c.code === currency)) {
      warnings.push(`${where}: Currency "${t('defaultCurrency')}" isn't a supported code — left blank.`);
      currency = '';
    }
    const kycRaw = t('kycStatus').toLowerCase();
    const kyc = (Object.keys(KYC_LABEL) as KYCStatus[]).find(k => k === kycRaw || KYC_LABEL[k].toLowerCase() === kycRaw
      || (k === 'verified' && kycRaw.startsWith('verif')) || (k === 'pending' && kycRaw.startsWith('pend')));
    if (kycRaw && !kyc) warnings.push(`${where}: KYC Status "${t('kycStatus')}" not recognised — set to Not Submitted.`);
    const termsN  = parseInt(cleanNum(t('defaultTerms')), 10);
    const creditN = parseFloat(cleanNum(t('creditLimit')));
    const lessN   = parseFloat(cleanNum(t('defaultLess')));
    const kycExpiry = parseImportDate(r.raw.kycExpiry);
    if (t('kycExpiry') && !kycExpiry) warnings.push(`${where}: KYC Expiry "${t('kycExpiry')}" isn't a date — left blank.`);
    const trade = types.includes('customer') || types.includes('supplier');

    // Same shape EntityFormModal.handleSave writes.
    const payload: Record<string, unknown> = {
      types, companyName: name,
      address: formatAddressOneLine(billTo), billTo, shipTo, shipToSameAsBillTo: shipSame,
      country: country('country') || null,
      defaultCurrency: currency || null,
      website: t('website') || null,
      defaultTerms: trade && termsN > 0 ? termsN : null,
      creditLimitUSD: types.includes('customer') && creditN > 0 ? creditN : null,
      defaultLessPercent: types.includes('customer') && lessN > 0 && lessN < 100 ? lessN : null,
      email: t('email').toLowerCase(),
      accountantEmail: t('accountantEmail').toLowerCase() || null,
      vatGstBtw: t('vatGstBtw') || null,
      phone: t('phone'),
      ownerName: t('ownerName'),
      ownerPhone: t('ownerPhone'),
      ownerEmail: t('ownerEmail').toLowerCase() || null,
      kycStatus: needsKYC(types) ? (kyc ?? 'not-submitted') : 'not-submitted',
      kycDocType: t('kycDocType') || null,
      kycDocNumber: t('kycDocNumber') || null,
      kycExpiryDate: kycExpiry || null,
      kycNotes: null,
      notes: t('notes') || null,
      isActive: !no(t('active')),
    };
    if (isService(types)) {
      payload.accountNumber = t('accountNumber') || null;
      if (types.includes('shipping'))  payload.shippingModes = [];
      if (types.includes('insurance')) { payload.coverageTypes = []; payload.agentName = null; }
    }
    rows.push({ ...base, payload, problem: '' });
  }
  return { rows, warnings };
}

